import {
  Building2,
  DollarSign,
  GitBranch,
  ListOrdered,
  ShieldCheck,
  Sparkles,
  PieChart,
  UserCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { SelectionMode } from "@/lib/api-types";

// Re-export so callers that don't already import from api-types can pull the
// type from the same module as the catalog metadata.
export type { SelectionMode } from "@/lib/api-types";

export interface StrategyMeta {
  mode: SelectionMode;
  label: string;
  shortDescription: string;
  longDescription: string;
  icon: LucideIcon;
}

export const STRATEGY_CATALOG: Record<SelectionMode, StrategyMeta> = {
  price: {
    mode: "price",
    label: "Lowest Price",
    shortDescription: "Cheapest available carrier",
    longDescription:
      "Compares quotes from all eligible carriers and dispatches to the lowest-priced option. Own-fleet quotes are computed from the Costs page inputs; optionally cap the maximum delivery fee.",
    icon: DollarSign,
  },
  ranked: {
    mode: "ranked",
    label: "Preferred Carrier",
    shortDescription: "Try carriers in a fixed order",
    longDescription:
      "Define an ordered list of preferred carriers. Dispatch attempts the top choice first, falling back to the next available carrier on failure.",
    icon: ListOrdered,
  },
  preferred_driver: {
    mode: "preferred_driver",
    label: "Preferred Driver",
    shortDescription: "Try own-fleet drivers in a fixed order",
    longDescription:
      "Define an ordered list of preferred drivers from your own fleet. Dispatch attempts the top driver first, falling back to the next available driver. Useful for VIP accounts, hazmat-certified drivers, or relationship-based routing.",
    icon: UserCheck,
  },
  reliability: {
    mode: "reliability",
    label: "Reliability Score",
    shortDescription: "Highest historical success rate",
    longDescription:
      "Ranks carriers by historical completion rate, cancellation rate, and on-time rate over a configurable lookback window.",
    icon: ShieldCheck,
  },
  dynamic: {
    mode: "dynamic",
    label: "Dynamic Dispatch",
    shortDescription: "AI-balanced multi-factor",
    longDescription:
      "Balances cost, reliability, and speed using configurable weights. The cost factor uses your Costs page inputs to compute own-fleet quotes and compare them against marketplace quotes.",
    icon: Sparkles,
  },
  weighted: {
    mode: "weighted",
    label: "Weighted Distribution",
    shortDescription: "Honor contracted volume splits",
    longDescription:
      "Distribute volume across carriers by configured percentages. Useful for honoring contracted commitments or balancing internal fleet against marketplace carriers.",
    icon: PieChart,
  },
  external_broker: {
    mode: "external_broker",
    label: "Tender to External Broker",
    shortDescription: "Hand the load off to a third-party broker",
    longDescription:
      "Tender to one or more brokers in order via API, webhook, email RFQ, EDI 204, or manual ops resolution. The first broker that accepts (and meets the optional max-quote cap) takes the load.",
    icon: Building2,
  },
  chained: {
    mode: "chained",
    label: "Chained Strategy",
    shortDescription: "Try multiple strategies in order",
    longDescription:
      "Walk an ordered sequence of sub-strategies, falling through on no-candidate or tender decline. Each step optionally has a CEL gate expression. The first step that matches wins.",
    icon: GitBranch,
  },
};

export const STRATEGY_MODES: SelectionMode[] = [
  "price",
  "ranked",
  "preferred_driver",
  "reliability",
  "dynamic",
  "weighted",
  "external_broker",
  "chained",
];

// The fallback strategy now lives on the server (organizations.default_strategy_id).
// Use `useDefaultStrategy()` from `@/lib/hooks/use-strategies` to read it.

export function isSelectionMode(value: string | null): value is SelectionMode {
  return value !== null && value in STRATEGY_CATALOG;
}

export interface MockCarrier {
  id: string;
  name: string;
}

export const MOCK_CARRIERS: MockCarrier[] = [
  { id: "own", name: "Own Fleet" },
  { id: "curri", name: "Curri Marketplace" },
  { id: "roadie", name: "Roadie" },
  { id: "uber", name: "Uber Direct" },
];
